import { call, put, takeLatest } from "redux-saga/effects";
import { sessionApi } from "../../services/session.api";
import {
  Session,
  ValidateSessionSuccessPayload,
} from "../../types/session.definition";
import { getAdminUrl, getLoginApplicationUrl } from "../../utils/config-utils";
import { clearPersistedPinLock } from "../../utils/pin-utils";
import { removeMedecine360SessionTokenFromStorage } from "../../utils/session-utils";
import {
  clearSession,
  getSessionFailure,
  getSessionRequest,
  getSessionSuccess,
  requestTerminateSessionRequest,
  validateSessionFailure,
  validateSessionRequest,
  validateSessionSuccess,
} from "../reducers/sessionSlice";

function* validateSessionSaga() {
  try {
    console.log("Starting session validation...");
    const response: ValidateSessionSuccessPayload = yield call(
      sessionApi.validateSession
    );
    console.log("Session validation successful:", response);

    yield put(
      validateSessionSuccess({
        ...response,
        sessionStatus: "VALID_SESSION",
      })
    );
    removeMedecine360SessionTokenFromStorage();
    yield put(getSessionRequest());
  } catch (error) {
    console.error("Session validation failed:", error);

    // Handle different types of errors
    let errorMessage = "Failed to validate session";
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "object" && error !== null) {
      // Handle structured error objects
      const errorObj = error as { code?: string; message?: string }; // Type assertion for error handling
      if (
        errorObj.code === "NETWORK_ERROR" ||
        errorObj.code === "TIMEOUT_ERROR"
      ) {
        errorMessage = "Network error - please check your connection";
      } else if (
        errorObj.code === "UNAUTHORIZED" ||
        errorObj.code === "FORBIDDEN"
      ) {
        errorMessage = "Authentication failed - please log in again";
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      }
    }

    console.log(
      "Dispatching validateSessionFailure with message:",
      errorMessage
    );
    yield put(validateSessionFailure(errorMessage));
  }
}

function* getSessionSaga() {
  try {
    console.log("Getting session data...");
    const session: Session = yield call(sessionApi.getSession);
    console.log("Session data retrieved successfully:", session);
    yield put(getSessionSuccess(session));
  } catch (error) {
    console.error("Failed to get session data:", error);

    // If getSession fails, the session might be invalid
    yield put(
      getSessionFailure(
        error instanceof Error ? error.message : "Failed to get session"
      )
    );
    // Set session status to invalid so user gets redirected to external auth
    yield put(validateSessionFailure("Session data could not be retrieved"));
  }
}

export function* requestTerminateSessionSaga() {
  // MED-1644: the next sign-in must not start locked.
  clearPersistedPinLock();
  try {
    yield call(sessionApi.terminateSession);
  } catch {
    // Ignore errors during logout - we're logging out anyway
  } finally {
    // Redirect only once the logout request has completed: navigating while it is in flight
    // makes the browser abort it (MED-1394).
    yield put(clearSession());
    // MED-1482: come back to the admin after re-authenticating, not to the
    // dashboard application.
    const redirectTo = getAdminUrl();
    window.location.href = getLoginApplicationUrl(redirectTo);
  }
}

export function* watchSession() {
  yield takeLatest(validateSessionRequest.type, validateSessionSaga);
  yield takeLatest(getSessionRequest.type, getSessionSaga);
  yield takeLatest(
    requestTerminateSessionRequest.type,
    requestTerminateSessionSaga
  );
}
